import React from "react";
import axios from "axios";
import { ArrowUpRight, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader } from "./Services";
import { BRAND } from "../lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  { value: "customer_acquisition", label: "Customer acquisition" },
  { value: "affiliate_partnership", label: "Affiliate partnership" },
  { value: "data_audience_strategy", label: "Data and audience strategy" },
  { value: "email_sms_campaign", label: "Email or SMS campaign support" },
  { value: "compliance_policy", label: "Compliance or policy enquiry" },
  { value: "general", label: "General enquiry" },
];

const CONTACT_CARDS = [
  { icon: Mail, t: "All enquiries", d: "Commercial, partnership and compliance — one inbox.", email: BRAND.contactEmail },
];

export default function Contact() {
  const [form, setForm] = React.useState({
    name: "", company: "", email: "", phone: "", market: "", interest: "", message: "", consent: false,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    document.title = "Contact PlayerReach";
  }, []);

  const update = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Please tell us a little about your enquiry.";
    if (!form.interest) e.interest = "Please choose an option.";
    if (!form.consent) e.consent = "Please confirm your consent.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Thank you. We will be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", market: "", interest: "", message: "", consent: false });
    } catch (err) {
      console.error(err);
      toast.error("We couldn't submit your enquiry. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Start a Conversation."
        subtitle="Tell us about your brand, your markets and your current acquisition challenge. A member of the PlayerReach team will respond with a focused, commercially grounded next step."
      />

      <section className="section-pad">
        <div className="container-pr">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Form */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              {success ? (
                <div className="card-flat p-10 text-center" data-testid="contact-success">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600">
                    <CheckCircle2 className="h-7 w-7" />
                  </span>
                  <h3 className="font-display mt-5 text-2xl font-semibold text-slate-900">Enquiry received.</h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">Thank you for contacting PlayerReach. We will review your message and respond from the appropriate team. In the meantime, you can explore our services and policies.</p>
                  <button onClick={() => setSuccess(false)} className="mt-6 btn-secondary text-sm" data-testid="contact-send-another">
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="card-flat p-7 lg:p-9 space-y-5" data-testid="contact-form" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name" error={errors.name}>
                      <Input value={form.name} onChange={update("name")} placeholder="Your full name" data-testid="contact-name" />
                    </Field>
                    <Field label="Company" optional>
                      <Input value={form.company} onChange={update("company")} placeholder="Company or brand" data-testid="contact-company" />
                    </Field>
                    <Field label="Email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={update("email")} placeholder="work@email.com" data-testid="contact-email" />
                    </Field>
                    <Field label="Phone" optional>
                      <Input value={form.phone} onChange={update("phone")} placeholder="+1 555 0100" data-testid="contact-phone" />
                    </Field>
                    <Field label="Market or GEO of interest" optional>
                      <Input value={form.market} onChange={update("market")} placeholder="e.g. UK, DE, LATAM" data-testid="contact-market" />
                    </Field>
                    <Field label="I am interested in" error={errors.interest}>
                      <Select value={form.interest} onValueChange={update("interest")}>
                        <SelectTrigger className="bg-white" data-testid="contact-interest"><SelectValue placeholder="Choose one" /></SelectTrigger>
                        <SelectContent>
                          {INTERESTS.map((i) => (
                            <SelectItem key={i.value} value={i.value} data-testid={`contact-interest-${i.value}`}>{i.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Message" error={errors.message}>
                    <Textarea rows={5} value={form.message} onChange={update("message")} placeholder="A few lines on your brand, markets and what you're exploring." data-testid="contact-message" />
                  </Field>

                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => update("consent")(!!v)} data-testid="contact-consent" />
                    <Label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                      I agree that PlayerReach may use the information submitted to respond to my enquiry in accordance with the <a href="/legal/privacy-policy" className="text-[#0052FF] hover:underline">Privacy Policy</a>.
                    </Label>
                  </div>
                  {errors.consent && <p className="text-sm text-red-600">{errors.consent}</p>}

                  <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed" data-testid="contact-submit">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : <>Submit Enquiry <ArrowUpRight className="h-4 w-4" /></>}
                  </button>
                </form>
              )}
            </div>

            {/* Side cards */}
            <div className="lg:col-span-5 order-1 lg:order-2 space-y-4">
              {CONTACT_CARDS.map((c, i) => (
                <div key={c.t} className="card-flat p-6" data-testid={`contact-card-${i}`}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-50 border border-slate-200 text-[#0052FF]">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <p className="font-display mt-4 font-semibold text-slate-900">{c.t}</p>
                  <p className="mt-1 text-sm text-slate-600">{c.d}</p>
                  <a href={`mailto:${c.email}`} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#0052FF] hover:underline">
                    <Mail className="h-3.5 w-3.5" /> {c.email}
                  </a>
                </div>
              ))}

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-6">
                <p className="overline">Registered office</p>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                  {BRAND.legalEntity}<br />
                  Formation No. {BRAND.formationNumber}<br />
                  {BRAND.address}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, optional, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {optional && <span className="text-slate-400 font-normal">(optional)</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
